/**
 * Short public URLs for the TripMaster domain.
 *
 *   /                 homepage
 *   /search           package search / results
 *   /packages         packages tab
 *   /hotels           hotels tab
 *   /flights          flights tab
 *   /package/:id      package details
 *   /package/view/:hotel/:checkIn/:checkOut
 *                     package details with a GA-visible hotel and stay dates
 *   /destination/:slug destination page
 *
 * HotelsRiver keeps /he/vacation-packages?siteId=...
 */

import { isTripMasterSurface } from "./tripMasterEnv";

const LOCALE_PREFIX_RE = /^\/(he|en|us|el)(?=\/|$)/;
const STAY_SEARCH_KEYS = ["checkIn", "checkOut", "depDate", "retDate"];
const STRIP_ALWAYS = ["siteId"];

export const TRIPMASTER_CLEAN_PATHS = Object.freeze({
  home: "/",
  search: "/search",
  packages: "/packages",
  hotels: "/hotels",
  flights: "/flights",
});

function normalizePath(pathname) {
  const raw = String(pathname || "/").trim() || "/";
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  if (withSlash.length > 1 && withSlash.endsWith("/")) {
    return withSlash.replace(/\/+$/, "") || "/";
  }
  return withSlash;
}

export function stripLocalePrefix(pathname) {
  return normalizePath(String(pathname || "/").replace(LOCALE_PREFIX_RE, "") || "/");
}

function searchToParams(search) {
  return new URLSearchParams(String(search || "").replace(/^\?/, ""));
}

function paramsToSearch(params) {
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function isPreservedOperationalPath(path) {
  return (
    path === "/termsAndConditions" ||
    path.startsWith("/termsAndConditions/") ||
    path === "/terms" ||
    path === "/agentTerms" ||
    path === "/contact" ||
    path.startsWith("/contact/") ||
    path === "/customer-portal" ||
    path.startsWith("/customer-portal/") ||
    path === "/booking" ||
    path.startsWith("/booking/") ||
    path === "/customer-chat" ||
    path.startsWith("/customer-chat/") ||
    path.startsWith("/payment/") ||
    path === "/packages/profile" ||
    path.startsWith("/packages/profile/") ||
    path === "/packages/success" ||
    path.startsWith("/packages/success/") ||
    path.startsWith("/offers/") ||
    path.startsWith("/q/") ||
    path === "/dev" ||
    path.startsWith("/dev/")
  );
}

function categoryFromCleanPath(path) {
  if (path === "/packages") return "packages";
  if (path === "/hotels" || path.startsWith("/hotels/")) return "hotels";
  if (path === "/flights" || path.startsWith("/flights/")) return "flights";
  if (path === "/search") return "packages";
  if (path === "/") return "home";
  return "";
}

function hasStaySearch(params) {
  return STAY_SEARCH_KEYS.some((key) => String(params.get(key) || "").trim());
}

const ANALYTICS_PACKAGE_PATH_RE =
  /^\/(?:vacation-packages\/)?package\/view\/([a-z0-9]+(?:-[a-z0-9]+)*)\/(\d{4}-\d{2}-\d{2})\/(\d{4}-\d{2}-\d{2})$/;

function parseTripMasterParts(pathname, search) {
  const path = stripLocalePrefix(pathname);
  const params = searchToParams(search);
  const analyticsMatch = path.match(ANALYTICS_PACKAGE_PATH_RE);
  const packageMatch = path.match(/^\/(?:vacation-packages\/)?package\/([^/]+)$/);
  const destinationMatch = path.match(/^\/(?:vacation-packages\/)?destination\/([^/]+)$/);
  const queryCategory = String(params.get("category") || "").trim().toLowerCase();
  const pathCategory = categoryFromCleanPath(path);
  const category =
    queryCategory ||
    pathCategory ||
    (path === "/vacation-packages" || path === "/vacation_packages" ? "home" : "");

  return {
    path,
    params,
    packageId: analyticsMatch
      ? "view"
      : packageMatch?.[1]
        ? decodeURIComponent(packageMatch[1])
        : null,
    analyticsPath: analyticsMatch
      ? `/package/view/${analyticsMatch[1]}/${analyticsMatch[2]}/${analyticsMatch[3]}`
      : null,
    destinationSlug: destinationMatch?.[1] ? decodeURIComponent(destinationMatch[1]) : null,
    category,
  };
}

export function toTripMasterLocation({ pathname = "/", search = "", hash = "" } = {}) {
  const { path, params, packageId, analyticsPath, destinationSlug, category } = parseTripMasterParts(
    pathname,
    search
  );

  for (const key of STRIP_ALWAYS) params.delete(key);

  if (isPreservedOperationalPath(path)) {
    return { pathname: path, search: paramsToSearch(params), hash: hash || "" };
  }

  let nextPath = TRIPMASTER_CLEAN_PATHS.home;
  if (analyticsPath) {
    nextPath = analyticsPath;
  } else if (packageId) {
    nextPath = `/package/${packageId}`;
  } else if (destinationSlug) {
    nextPath = `/destination/${destinationSlug}`;
  } else if (category === "flights") {
    nextPath = TRIPMASTER_CLEAN_PATHS.flights;
  } else if (category === "hotels") {
    nextPath = TRIPMASTER_CLEAN_PATHS.hotels;
  } else if (hasStaySearch(params) && category !== "hotels" && category !== "flights") {
    nextPath = TRIPMASTER_CLEAN_PATHS.search;
  } else if (category === "packages") {
    nextPath = TRIPMASTER_CLEAN_PATHS.packages;
  } else {
    nextPath = TRIPMASTER_CLEAN_PATHS.home;
  }

  params.delete("category");
  return { pathname: nextPath, search: paramsToSearch(params), hash: hash || "" };
}

export function tripMasterLocationsEqual(a, b) {
  return (
    normalizePath(a?.pathname) === normalizePath(b?.pathname) &&
    String(a?.search || "") === String(b?.search || "") &&
    String(a?.hash || "") === String(b?.hash || "")
  );
}

function splitHref(raw) {
  const text = String(raw || "/");
  const hashIndex = text.indexOf("#");
  const withoutHash = hashIndex >= 0 ? text.slice(0, hashIndex) : text;
  const hash = hashIndex >= 0 ? text.slice(hashIndex) : "";
  const queryIndex = withoutHash.indexOf("?");
  return {
    pathname: queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash,
    search: queryIndex >= 0 ? withoutHash.slice(queryIndex) : "",
    hash,
  };
}

export function toTripMasterHref(to) {
  if (to && typeof to === "object") {
    const next = toTripMasterLocation({
      pathname: to.pathname || "/",
      search: to.search || "",
      hash: to.hash || "",
    });
    return { ...to, ...next };
  }
  const parts = splitHref(to);
  const next = toTripMasterLocation(parts);
  return `${next.pathname}${next.search}${next.hash}`;
}

export function rewritePublicHrefForCurrentSurface(to) {
  if (!isTripMasterSurface()) return to;
  return toTripMasterHref(to);
}

export function resolveTripMasterCategoryFromPath(pathname) {
  if (!isTripMasterSurface()) return "";
  const { path, packageId, destinationSlug, category } = parseTripMasterParts(pathname, "");
  if (packageId || destinationSlug) return "packages";
  return category || categoryFromCleanPath(path) || "home";
}

export function isTripMasterCleanPublicPath(pathname) {
  const path = stripLocalePrefix(pathname);
  if (path === "/") return true;
  return (
    path === "/search" ||
    path === "/packages" ||
    path === "/hotels" ||
    path.startsWith("/hotels/") ||
    path === "/flights" ||
    path.startsWith("/flights/") ||
    path.startsWith("/package/") ||
    path.startsWith("/destination/") ||
    path === "/vacation-packages" ||
    path.startsWith("/vacation-packages/") ||
    path === "/vacation_packages" ||
    path.startsWith("/vacation_packages/")
  );
}

export function tripMasterPathForCategory(category) {
  const value = String(category || "").trim().toLowerCase();
  if (value === "hotels") return TRIPMASTER_CLEAN_PATHS.hotels;
  if (value === "flights") return TRIPMASTER_CLEAN_PATHS.flights;
  if (value === "packages") return TRIPMASTER_CLEAN_PATHS.packages;
  return TRIPMASTER_CLEAN_PATHS.home;
}

export function stripTripMasterEngineParams(search) {
  const params = searchToParams(search);
  params.delete("category");
  params.delete("stage");
  params.delete("hotelId");
  params.delete("ctx");
  params.delete("hotelPackageId");
  params.delete("siteId");
  return paramsToSearch(params);
}
